'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ForumPost {
  id: number;
  author: string;
  avatar: string;
  avatarAlt: string;
  title: string;
  content: string;
  category: string;
  replies: number;
  views: number;
  likes: number;
  timestamp: string;
  tags: string[];
}

interface CommunityForumProps {
  className?: string;
}

export default function CommunityForum({ className = '' }: CommunityForumProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const forumPosts: ForumPost[] = [
    {
      id: 1,
      author: 'Rajesh Kumar',
      avatar:
        'https://img.rocket.new/generatedImages/rocket_gen_img_1ee047f3e-1763293623246.png',
      avatarAlt:
        'Professional headshot of Indian man in blue shirt smiling at camera',
      title:
        'Best practices for installing ParkPing in apartment complexes',
      content:
        'We recently installed ParkPing cards for all 50 residents in our society. Here are some tips that made the process smooth...',
      category: 'Tips & Tricks',
      replies: 23,
      views: 456,
      likes: 34,
      timestamp: '2 hours ago',
      tags: ['installation', 'society', 'bulk-order'],
    },
    {
      id: 2,
      author: 'Priya Sharma',
      avatar:
        'https://img.rocket.new/generatedImages/rocket_gen_img_1105289b0-1763294330430.png',
      avatarAlt:
        'Young Indian woman with long black hair in professional attire smiling',
      title: 'How ParkPing saved me from a parking dispute',
      content:
        'Yesterday someone blocked my car and I was able to reach them instantly without any confrontation. This is my experience...',
      category: 'Success Stories',
      replies: 15,
      views: 892,
      likes: 67,
      timestamp: '5 hours ago',
      tags: ['experience', 'success', 'conflict-resolution'],
    },
    {
      id: 3,
      author: 'Amit Patel',
      avatar:
        'https://img.rocket.new/generatedImages/rocket_gen_img_1432b9929-1763293721888.png',
      avatarAlt:
        'Middle-aged Indian man with glasses in casual shirt outdoors',
      title: 'Question about privacy settings',
      content:
        'Can someone explain how the call masking works? I want to make sure my number stays private...',
      category: 'Questions',
      replies: 8,
      views: 234,
      likes: 12,
      timestamp: '1 day ago',
      tags: ['privacy', 'security', 'help'],
    },
    {
      id: 4,
      author: 'Sneha Reddy',
      avatar:
        'https://img.rocket.new/generatedImages/rocket_gen_img_15f4e23f6-1763300607163.png',
      avatarAlt:
        'Young professional Indian woman in white blazer with confident smile',
      title:
        'Integration with housing society management app',
      content:
        'Has anyone tried integrating ParkPing with their society management software? Looking for recommendations...',
      category: 'Technical',
      replies: 19,
      views: 567,
      likes: 28,
      timestamp: '2 days ago',
      tags: ['integration', 'api', 'society-management'],
    },
    {
      id: 5,
      author: 'Vikram Singh',
      avatar:
        'https://images.unsplash.com/photo-1613984210709-c641c728e4df',
      avatarAlt:
        'Indian man with beard in casual denim jacket smiling outdoors',
      title:
        'Creative ways to promote ParkPing in your community',
      content:
        "I convinced 30 neighbors to get ParkPing cards. Here's how I did it and the benefits we're seeing...",
      category: 'Community',
      replies: 31,
      views: 1203,
      likes: 89,
      timestamp: '3 days ago',
      tags: ['community', 'promotion', 'advocacy'],
    },
  ];

  const categories = [
    'All',
    'Tips & Tricks',
    'Success Stories',
    'Questions',
    'Technical',
    'Community',
  ];

  const filteredPosts =
    selectedCategory === 'All'
      ? forumPosts
      : forumPosts.filter(
          (post) => post.category === selectedCategory
        );

  return (
    <section className={`py-16 bg-white ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Community Forum
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Connect with other ParkPing users, share experiences, and learn
              from the community
            </p>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-3 mb-8 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  selectedCategory === category
                    ? 'bg-[#42AF47] text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Posts */}
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <div
                key={post.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#42AF47]/40 hover:shadow-md transition"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage
                      src={post.avatar}
                      alt={post.avatarAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 hover:text-[#42AF47] cursor-pointer transition">
                      {post.title}
                    </h3>

                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-sm text-gray-600">
                        {post.author}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-400">
                        {post.timestamp}
                      </span>
                      <span className="text-xs px-2 py-1 bg-[#42AF47]/10 text-[#42AF47] rounded">
                        {post.category}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                      {post.content}
                    </p>

                    <div className="flex flex-wrap gap-2 my-3">
                      {post.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-[#42AF47]/10 hover:text-[#42AF47] cursor-pointer transition"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center space-x-6 text-sm text-gray-600">
                      <button className="flex items-center gap-1 hover:text-[#42AF47] transition">
                        <Icon
                          name="ChatBubbleLeftIcon"
                          size={16}
                        />
                        <span>{post.replies} replies</span>
                      </button>

                      <button className="flex items-center gap-1 hover:text-[#42AF47] transition">
                        <Icon name="HeartIcon" size={16} />
                        <span>{post.likes} likes</span>
                      </button>

                      <div className="flex items-center gap-1">
                        <Icon name="EyeIcon" size={16} />
                        <span>{post.views} views</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <button className="px-6 py-3 bg-[#42AF47] text-white rounded-lg hover:brightness-110 hover:-translate-y-0.5 transition inline-flex items-center gap-2">
              <Icon name="PlusCircleIcon" size={20} />
              <span>Start a New Discussion</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
